import { useEffect, useState } from "react";
import { Radio, Plus, Trash2, Pencil, Loader2, CheckCircle2, AlertTriangle, Users, ArrowLeftRight, CopyPlus, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getWorkspace, setWorkspace } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChannelsOverview } from "@/components/ChannelsOverview";
import { loadWorkspaces } from "@/lib/useWorkspaces";
import { Mail } from "lucide-react";

function InviteDialog({ open, onClose, channels }) {
  const [form, setForm] = useState({ email: "", name: "", workspace_ids: [] });
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setForm((f) => ({ ...f, workspace_ids: f.workspace_ids.includes(id) ? f.workspace_ids.filter((x) => x !== id) : [...f.workspace_ids, id] }));
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workspaces/invite", form);
      if (r.data.email_sent) toast.success(`Invito inviato a ${r.data.email}`);
      else toast.warning(`Utente creato ma email non inviata (${r.data.email_error || "errore"}). Password temporanea: ${r.data.temp_password}`, { duration: 20000 });
      setForm({ email: "", name: "", workspace_ids: [] });
      onClose(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invita operatore</DialogTitle>
          <DialogDescription>L'operatore riceve un'email con link di accesso, email e password temporanea, con l'elenco dei canali assegnati.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="operatore@azienda.it" className="bg-white border-slate-200" data-testid="invite-email" /></div>
          <div className="space-y-1.5"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome e cognome" className="bg-white border-slate-200" data-testid="invite-name" /></div>
          <div className="space-y-1.5">
            <Label>Canali a cui dare accesso</Label>
            <div className="flex flex-wrap gap-2">
              {channels.map((w) => {
                const on = form.workspace_ids.includes(w.id);
                return <button key={w.id} onClick={() => toggle(w.id)} data-testid={`invite-channel-${w.id}`} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#1E4FD8] text-white border-[#1E4FD8]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>{w.name}</button>;
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="border-slate-200" onClick={() => onClose(false)}>Annulla</Button>
          <Button onClick={send} disabled={busy || !form.email || form.workspace_ids.length === 0} className="bg-[#1E4FD8] hover:bg-[#173FB0] text-white" data-testid="invite-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Invia invito</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CRED_FIELDS = [
  ["wa_phone_number_id", "Phone Number ID", "ID numero di telefono (App Meta → WhatsApp → Configurazione API)"],
  ["wa_waba_id", "WABA ID", "ID account WhatsApp Business"],
  ["wa_access_token", "Token di accesso permanente", "Business Manager → Utenti di sistema → Genera token", true],
  ["wa_app_id", "App ID", "App Meta → Impostazioni → Di base"],
  ["wa_app_secret", "App Secret", "Chiave segreta dell'app (firma webhook)", true],
  ["wa_verify_token", "Token di verifica webhook", "Stringa a tua scelta, identica nel pannello Meta"],
  ["wa_graph_version", "Versione Graph API", "es. v26.0 — vuoto = versione della piattaforma (GRAPH_API_VERSION)"],
];

const COLORS = ["#1E4FD8", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#0EA5E9", "#EC4899"];

function CloneDialog({ ws, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [opts, setOpts] = useState({ templates: true, automations: true, clusters: true, settings: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (ws) setName(`${ws.name} (copia)`); }, [ws]);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${ws.id}/clone`, { name: name.trim(), color: COLORS[(COLORS.indexOf(ws.color) + 1) % COLORS.length], ...opts });
      const c = r.data.counts || {};
      toast.success(`Canale «${r.data.name}» creato: ${c.templates ?? 0} template, ${c.automations ?? 0} automazioni, ${c.clusters ?? 0} cluster`, { duration: 8000 });
      onCreated(r.data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const items = [["templates", "Template messaggi", "copiati come bozze da richiedere a Meta nel nuovo WABA"], ["automations", "Automazioni", "copiate disattivate"],
                 ["clusters", "Cluster", "solo le definizioni, senza contatti"], ["settings", "Impostazioni e base di conoscenza IA", "assistente, FAQ, orari; esclusi email avvisi e report cliente"]];
  return (
    <Dialog open={!!ws} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="clone-dialog">
        <DialogHeader>
          <DialogTitle>Clona canale «{ws?.name}»</DialogTitle>
          <DialogDescription>Crea un nuovo canale pronto all'uso partendo da questa configurazione. Contatti, chat e storico non vengono copiati; le credenziali Meta vanno inserite dopo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nome del nuovo canale</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white border-slate-200" data-testid="clone-name" /></div>
          {items.map(([k, label, hint]) => (
            <label key={k} className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer">
              <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} className="mt-1 accent-[#1E4FD8]" data-testid={`clone-opt-${k}`} />
              <span><span className="block text-sm text-slate-800">{label}</span><span className="block text-[11px] text-slate-500">{hint}</span></span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="border-slate-200" onClick={onClose}>Annulla</Button>
          <Button onClick={run} disabled={busy || !name.trim()} className="bg-[#1E4FD8] hover:bg-[#173FB0] text-white" data-testid="clone-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CopyPlus className="h-4 w-4 mr-2" /> Clona canale</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChannelCard({ ws, onEdit, onDelete, onSwitch, onClone, onConnectMeta, isCurrent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3 shadow-sm" data-testid={`channel-card-${ws.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: ws.color + "22", color: ws.color }}><Radio className="h-5 w-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate" data-testid={`channel-name-${ws.id}`}>{ws.name}</p>
          <p className="text-xs text-slate-500 truncate">{ws.phone_display ? `${ws.phone_display} · ${ws.verified_name || ""}` : "Numero non ancora collegato"}</p>
        </div>
        {isCurrent && <Badge className="bg-[#1E4FD8]/10 text-[#1E4FD8] border-0 text-[10px]" data-testid={`channel-current-${ws.id}`}>Attivo</Badge>}
      </div>
      <div className="flex items-center gap-2 text-xs">
        {ws.wa_configured
          ? <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`channel-status-${ws.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> WhatsApp configurato</span>
          : <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`channel-status-${ws.id}`}><AlertTriangle className="h-3.5 w-3.5" /> Credenziali Meta mancanti</span>}
        {ws.is_default && <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">Principale</Badge>}
      </div>
      <div className="flex gap-2 mt-auto pt-1">
        <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200" onClick={() => onEdit(ws)} data-testid={`channel-edit-${ws.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Configura</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200" onClick={() => onClone(ws)} data-testid={`channel-clone-${ws.id}`}><CopyPlus className="h-3.5 w-3.5 mr-1" /> Clona</Button>
        {!isCurrent && <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200" onClick={() => onSwitch(ws)} data-testid={`channel-switch-${ws.id}`}><ArrowLeftRight className="h-3.5 w-3.5 mr-1" /> Passa a</Button>}
        {!ws.is_default && <Button size="sm" className="h-8 text-xs bg-[#1877F2] hover:bg-[#145dbf] text-white" onClick={() => onConnectMeta(ws)} data-testid={`channel-connect-meta-${ws.id}`}><Link2 className="h-3.5 w-3.5 mr-1" /> Collega WhatsApp Business</Button>}
        {!ws.is_default && <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600 hover:bg-red-50 ml-auto" onClick={() => onDelete(ws)} data-testid={`channel-delete-${ws.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
    </div>
  );
}

function ChannelDialog({ wsId, users, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}`).then((r) => {
      setDetail(r.data);
      setForm({ name: r.data.name, color: r.data.color, sms_sender: r.data.credentials.sms_sender.from_env ? "" : r.data.credentials.sms_sender.value,
                monthly_quota: r.data.credentials.monthly_quota.value || "", alert_email: r.data.alert_email || "" });
      setMembers(r.data.member_ids || []);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [wsId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, color: form.color, sms_sender: form.sms_sender ?? "", monthly_quota: form.monthly_quota ? Number(form.monthly_quota) : undefined, alert_email: form.alert_email ?? "" };
      CRED_FIELDS.forEach(([k]) => { if (form[k] !== undefined) body[k] = form[k]; });
      const r = await api.put(`/workspaces/${wsId}`, body);
      await api.put(`/workspaces/${wsId}/members`, { user_ids: members });
      if (r.data.warning) toast.warning(r.data.warning); else toast.success("Canale aggiornato");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  const operators = users.filter((u) => u.role !== "admin");

  return (
    <Dialog open={!!wsId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="channel-dialog">
        <DialogHeader>
          <DialogTitle>Configura canale</DialogTitle>
          <DialogDescription>Ogni canale ha il proprio numero WhatsApp, Business Manager e dati separati (rubrica, campagne, automazioni, report).</DialogDescription>
        </DialogHeader>
        {!detail ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-[1fr_auto] gap-4">
              <div className="space-y-1.5">
                <Label>Nome canale</Label>
                <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-white border-slate-200" data-testid="channel-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Colore</Label>
                <div className="flex gap-1.5">
                  {COLORS.map((c) => <button key={c} onClick={() => set("color", c)} className={`h-7 w-7 rounded-full border-2 ${form.color === c ? "border-slate-800" : "border-transparent"}`} style={{ backgroundColor: c }} data-testid={`channel-color-${c.slice(1)}`} />)}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-900">Credenziali Meta (WhatsApp Cloud API)</p>
              {detail.is_default && <p className="text-xs text-slate-500">Il canale principale usa le credenziali dei Secrets dell'app; compilando un campo qui lo sovrascrivi.</p>}
              <div className="grid sm:grid-cols-2 gap-3">
                {CRED_FIELDS.map(([k, label, hint, secret]) => {
                  const c = detail.credentials[k];
                  return (
                    <div key={k} className="space-y-1">
                      <Label className="text-xs">{label} {c.set && <span className={`ml-1 font-normal ${c.from_env ? "text-slate-400" : "text-emerald-600"}`}>· {c.from_env ? "da Secrets" : "impostato"}</span>}</Label>
                      <Input type={secret ? "password" : "text"} placeholder={c.set ? (secret ? c.value : c.value) : "—"}
                             value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="bg-white border-slate-200 h-9 text-sm" data-testid={`channel-cred-${k}`} />
                      <p className="text-[11px] text-slate-500">{hint}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500">Webhook: usa lo stesso URL <code>{window.location.origin}/api/whatsapp/webhook</code> nel pannello Meta di questo Business Manager con il token di verifica del canale; gli eventi vengono smistati automaticamente in base al Phone Number ID.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Mittente SMS (SMSFactor, max 11 caratteri)</Label>
                <Input value={form.sms_sender ?? ""} onChange={(e) => set("sms_sender", e.target.value)} placeholder={detail.credentials.sms_sender.from_env ? `${detail.credentials.sms_sender.value} (da Secrets)` : "Es. ViviClub"} className="bg-white border-slate-200 h-9" data-testid="channel-sms-sender" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Quota mensile messaggi</Label>
                <Input type="number" value={form.monthly_quota ?? ""} onChange={(e) => set("monthly_quota", e.target.value)} className="bg-white border-slate-200 h-9" data-testid="channel-quota" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Email avvisi del canale (quota/budget, qualità numero, webhook, crediti SMS, promemoria campagne)</Label>
                <Input type="email" value={form.alert_email ?? ""} onChange={(e) => set("alert_email", e.target.value)} placeholder={detail.alert_email_default ? `Vuoto = ${detail.alert_email_default}` : "email@azienda.it"} className="bg-white border-slate-200 h-9" data-testid="channel-alert-email" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Users className="h-4 w-4" /> Operatori con accesso</p>
              {operators.length === 0 ? <p className="text-xs text-slate-500">Nessun operatore: gli amministratori vedono sempre tutti i canali. Crea operatori da Integrazioni → Gestione utenti.</p> : (
                <div className="flex flex-wrap gap-2">
                  {operators.map((u) => {
                    const on = members.includes(u.id);
                    return (
                      <button key={u.id} onClick={() => setMembers((m) => on ? m.filter((x) => x !== u.id) : [...m, u.id])} data-testid={`channel-member-${u.id}`}
                              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#1E4FD8] text-white border-[#1E4FD8]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                        {u.name || u.email}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="border-slate-200" onClick={onClose}>Annulla</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1E4FD8] hover:bg-[#173FB0] text-white" data-testid="channel-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Canali() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [cloning, setCloning] = useState(null);
  const current = getWorkspace();

  const load = () => {
    api.get("/workspaces").then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get("/auth/users").then((r) => setUsers(r.data || [])).catch(() => {});
    loadWorkspaces(true);
  };
  useEffect(load, []);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/workspaces", { name: newName.trim(), color: COLORS[items.length % COLORS.length] });
      toast.success(`Canale «${r.data.name}» creato`);
      setCreating(false); setNewName(""); load(); setEditing(r.data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const remove = async (ws) => {
    if (!window.confirm(`Eliminare il canale «${ws.name}» e TUTTI i suoi dati (rubrica, campagne, automazioni, chat)? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/workspaces/${ws.id}`);
      toast.success("Canale eliminato");
      if (ws.id === current) { setWorkspace("default"); window.location.href = "/"; return; }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const switchTo = (ws) => { setWorkspace(ws.id); window.location.href = "/"; };
  // Collega WhatsApp Business: apre Account Meta nel contesto del canale scelto (Embedded Signup o token manuale)
  const connectMeta = (ws) => { setWorkspace(ws.id); window.location.href = "/account-meta"; };

  return (
    <div className="space-y-8" data-testid="channels-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900">Canali</h1>
          <p className="text-slate-500 mt-1">Un canale = un numero WhatsApp con il suo Business Manager e i suoi dati separati. Passa da uno all'altro dalla sidebar.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setInviting(true)} className="border-slate-200" data-testid="invite-operator-button"><Mail className="h-4 w-4 mr-2" /> Invita operatore</Button>
          <Button onClick={() => setCreating(true)} className="bg-[#1E4FD8] hover:bg-[#173FB0] text-white" data-testid="channel-create-button"><Plus className="h-4 w-4 mr-2" /> Nuovo canale</Button>
        </div>
      </div>
      <ChannelsOverview />
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((ws) => <ChannelCard key={ws.id} ws={ws} isCurrent={ws.id === current} onEdit={(w) => setEditing(w.id)} onDelete={remove} onSwitch={switchTo} onClone={setCloning} onConnectMeta={connectMeta} />)}
      </div>
      <CloneDialog ws={cloning} onClose={() => setCloning(null)} onCreated={(id) => { setCloning(null); load(); setEditing(id); }} />
      <InviteDialog open={inviting} channels={items} onClose={(ok) => { setInviting(false); if (ok) load(); }} />

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="channel-create-dialog">
          <DialogHeader><DialogTitle>Nuovo canale</DialogTitle><DialogDescription>Crea un ambiente autonomo per un altro numero WhatsApp. Le credenziali Meta si inseriscono subito dopo.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label>Nome canale</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Es. Studio Nouvelle Salerno" className="bg-white border-slate-200" data-testid="channel-create-name" onKeyDown={(e) => e.key === "Enter" && create()} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="border-slate-200" onClick={() => setCreating(false)}>Annulla</Button>
            <Button onClick={create} disabled={busy || !newName.trim()} className="bg-[#1E4FD8] hover:bg-[#173FB0] text-white" data-testid="channel-create-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea canale"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ChannelDialog wsId={editing} users={users} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
